/**
 * Submits a PA for a CMM order in QA Updated / Submission Error via submit-pa.
 *
 * Builds the same OncoEMR request shape used by OncoEMR re-runs, then POSTs to
 * {@link API_ENDPOINTS.RE_RUN_ONCO_EMR_WORKFLOW} (`/submit-pa`).
 */

import { controlToastState } from "risa-oasis-ui_v2";
import {
  getCmmInputDataForOrder,
  reRunWorkflows,
} from "../../../api/bigQuery/nycbsPharmaOrders";
import { OrganizationType } from "../../../enums/organizationTypes";
import { logError } from "../../../utils/customLogger";

/**
 * Builds the org-specific fields for a submit-pa request from order details.
 *
 * @input orderData - CMM input row details from getCmmInputDataForOrder
 * @return Type and prescriber fields to merge into the base payload
 */
const buildTypePayload = (orderData: Record<string, any>): Record<string, unknown> => {
  const prescriberName = orderData?.provider_first_name
    ? `${orderData.provider_first_name} ${orderData.provider_last_name || ""}`.trim()
    : "";

  if (orderData?.type === "Internal") {
    return {
      type: "Internal",
      prescriber_name: prescriberName,
      sftp_rx_bin: orderData?.sftp_rx_bin,
      sftp_member_id: orderData?.sftp_member_id,
    };
  }

  if (orderData?.type === "External") {
    return {
      type: "External",
      prescriber_name: prescriberName,
    };
  }

  return {};
};

/**
 * Builds the submit-pa body for an existing order (QA Updated / retry submit).
 *
 * @input orderData - CMM input row details
 * @input orgIdOverride - Optional org id override from the UI context
 * @return Payload accepted by reRunWorkflows / submit-pa
 */
const buildSubmitPaPayload = (
  orderData: Record<string, any>,
  orgIdOverride?: string,
): Record<string, unknown> => {
  const orgId = orgIdOverride || orderData?.org_id;
  const drugName = orderData?.drug_name_onco_emr || orderData?.drug_name;
  const typePayload = buildTypePayload(orderData);

  if (orgId === OrganizationType.ASTERA) {
    return {
      drug_name: drugName,
      identifier: orderData?.identifier,
      patient_dob: orderData?.patient_dob,
      patient_last_name: orderData?.patient_last_name,
      external_source_identifier: orderData?.external_source_identifier,
      org_id: orgId,
      is_new_request: false,
      ...typePayload,
    };
  }

  return {
    drug_name: drugName,
    identifier: orderData?.identifier,
    patient_mrn: orderData?.patient_mrn,
    org_id: orgId,
    is_new_request: false,
    ...typePayload,
  };
};

/**
 * Fetches order details and POSTs to submit-pa for QA Updated / Retry Submit.
 *
 * @input orderId - PA order identifier
 * @input onSuccess - Optional callback after a successful submit (e.g. refresh table)
 * @input orgIdOverride - Optional org id when the table context already knows it
 */
export const executeSubmitPa = async (
  orderId: string,
  onSuccess?: () => void,
  orgIdOverride?: string,
): Promise<void> => {
  controlToastState("submit-pa-initiated");

  try {
    const orderResponse = await getCmmInputDataForOrder(orderId);
    const orderData = orderResponse?.["details"];

    if (!orderData) {
      throw new Error("Failed to fetch order data for submit-pa");
    }

    await reRunWorkflows(buildSubmitPaPayload(orderData, orgIdOverride));

    controlToastState("submit-pa-success");
    onSuccess?.();
  } catch (error) {
    logError(error as Error, "Error in executeSubmitPa");
    controlToastState("submit-pa-error");
  }
};
